'use client';

import { useTranslations } from 'next-intl';
import React from 'react';

import { LandingWrapper } from '@ui/components/features/landing/landingWrapper/LandingWrapper';
import { VisualSection } from '@ui/components/features/landing/visualSection/VisualSection';
import { HomeSearchBar, type HomeSearchBarLocation } from '@website/components/HomeSearchBar';

interface AuthenticatedLandingSectionProps {
  /** Resolved user location (name + coordinates) used to prefill the search bar. */
  currentLocation?: HomeSearchBarLocation;
  showGenericMessage?: boolean;
  onSearch: (query: string, location?: HomeSearchBarLocation) => void;
}

export const AuthenticatedLandingSection: React.FC<AuthenticatedLandingSectionProps> = ({
  currentLocation,
  showGenericMessage = false,
  onSearch
}) => {
  const t = useTranslations();

  return (
    <LandingWrapper disableMinHeight>
      {/* Left side - Search section */}
      <div className="flex-1 w-full">
        <h1 className="text-3xl md:text-5xl font-bold text-gray-900 mb-8">
          {showGenericMessage || !currentLocation ? (
            (() => {
              const text = t('HomePage.landing.titleNearYou');
              // Split on "mentors" (works for both English and French)
              const parts = text.split(/(mentors)/i);
              return (
                <>
                  {parts.map((part, index) =>
                    part.toLowerCase() === 'mentors' ? (
                      <span key={index} className="text-primary-500">
                        {part}
                      </span>
                    ) : (
                      <span key={index}>{part}</span>
                    )
                  )}
                </>
              );
            })()
          ) : (
            <>
              {t('HomePage.landing.titleWithLocation')} <span className="text-primary-500">{currentLocation.name}</span>
            </>
          )}
        </h1>
        <HomeSearchBar onSearch={onSearch} initialLocation={currentLocation} />
      </div>

      {/* Right side - Visual content */}
      <VisualSection
        musicDancesLabel={t('HomePage.landing.musicDancesLabel')}
        personalDevelopmentLabel={t('HomePage.landing.personalDevelopmentLabel')}
        healthWellnessLabel={t('HomePage.landing.healthWellnessLabel')}
        photoCount={3}
      />
    </LandingWrapper>
  );
};
